/** A proxy that wraps every function with debug information.
 * - What function is being called
 * - With what value it returned/errored
 *
 *  Useful if you want to debug class/record method calls. */
export namespace DebugProxy {
    type ResultType = 'error' | 'return';

    function handleResult<T>(options: {
        displayName: string;
        functionKey: string;
        resultType: ResultType;
        value: T;
    }): T {
        console.debug(
            ` => ${options.displayName}.${options.functionKey}(...) ${options.resultType}ed: `,
            options.value,
        );

        if (options.resultType === 'error') throw options.value;
        return options.value;
    }

    export function create<T extends {}>(object: T, displayName: string = 'debugProxy'): T {
        const entries: [string, unknown][] = [];
        const seen = new Set<string>();
        let current: object | null = object;
        while (current && current !== Object.prototype) {
            for (const key of Object.getOwnPropertyNames(current)) {
                if (key === 'constructor' || seen.has(key)) continue;
                seen.add(key);
                const value = (object as any)[key];
                entries.push([key, typeof value === 'function' ? value.bind(object) : value]);
            }
            current = Object.getPrototypeOf(current);
        }

        const proxy = Object.fromEntries(
            entries.map(([key, value]: [string, unknown]) => [
                key,
                typeof value === 'function'
                    ? (...args: unknown[]) => {
                          console.debug(`${displayName} calling fn "${key}" with args: `, args);

                          function handleFnResult<T>(resultType: ResultType, value: T): T {
                              return handleResult({
                                  displayName,
                                  value,
                                  resultType,
                                  functionKey: key,
                              });
                          }

                          let res;
                          try {
                              res = (value as Function)(...args);
                              // Handle sync result
                              if (res instanceof Promise === false) {
                                  return handleFnResult('return', res);
                              }
                          } catch (error) {
                              return handleFnResult('error', error);
                          }

                          // Handle promise
                          return (res as Promise<unknown>)
                              .then(x => handleFnResult('return', x))
                              .catch(error => handleFnResult('error', error));
                      }
                    : value,
            ]),
        );

        return proxy as T;
    }
}

export function debugValue<T>(value: T): T {
    if (typeof (globalThis as any).window !== 'undefined') {
        console.debug('DEBUG: ', value);
    } else {
        console.dir({ DEBUG: value }, { depth: null });
    }
    return value;
}

/** @usage - Don't directly construct this class */
export class DebugTimer {
    constructor(
        private readonly state: {
            finished: boolean;
            label: string;
            start: Date;
        },
    ) {}

    private static formatDelta(start: Date | number, end: Date | number) {
        const startMs = typeof start === 'number' ? start : start.valueOf();
        const endMs = typeof end === 'number' ? end : end.valueOf();

        const delta = endMs - startMs;

        return delta >= 1000 ? `${delta / 1000} s` : `${delta} ms`;
    }

    static start(label: string) {
        const timer = new DebugTimer({
            finished: false,
            label,
            start: new Date(),
        });

        return {
            end() {
                if (timer.state.finished) {
                    throw new Error(`Timer "${label}" is already finished.`);
                }

                console.debug(
                    `⏱️ [${label}] took`,
                    DebugTimer.formatDelta(timer.state.start, Date.now()),
                );
                timer.state.finished = true;
            },
        } as const;
    }

    static async promise<T>(label: string, promise: Promise<T>): Promise<T> {
        const timer = DebugTimer.start(label);

        return promise.finally(() => {
            timer.end();
        });
    }
}
